import { currentLoggedUserVar } from '@/shared/api/apollo/cache';
import { SideMenuContext } from '@/shared/model';
import { NAV_COLORS } from '@/shared/ui/components/SideMenus/navPalette';
import { Small } from '@/shared/ui/components/Typography';
import { useReactiveVar } from '@apollo/client';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { alpha, IconButton, Stack, Switch, Tooltip } from '@mui/material';
import { useContext } from 'react';

/**
 * The controls that decide how the menu behaves, at the head of the menu itself.
 *
 * Two separate things, which is why there are two controls:
 *   - the pin decides whether the menu stays open and holds its own space
 *   - Auto Hide decides whether an unpinned menu opens when the pointer reaches the rail
 *
 * With Auto Hide off, an unpinned menu opens only when the toggle beside it is clicked — for
 * anyone who finds a menu that moves under the pointer more startling than useful.
 *
 * The collapse button is a shortcut, not a third setting: it closes the menu from the pane's own
 * edge, so a wide open menu does not force the pointer back across to the rail.
 */
const SidebarPaneHeader = () => {
  const { isPinned, togglePinned, isAutoHideEnabled, toggleAutoHide, setDrawerOpen } = useContext(SideMenuContext);
  const currentUser = useReactiveVar(currentLoggedUserVar);
  const isMock = Boolean(currentUser?.relyingParty?.isMock);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 1,
        paddingRight: 0.5,
        paddingTop: isMock ? 2 : 0.5,
        paddingBottom: 0.5,
        borderBottom: `1px solid ${alpha(NAV_COLORS.onDark, 0.14)}`
      }}
    >
      <Tooltip title={isPinned ? 'Unpin menu' : 'Pin menu open'} placement="bottom-start">
        <IconButton
          onClick={togglePinned}
          size="small"
          aria-label={isPinned ? 'Unpin menu' : 'Pin menu open'}
          sx={{
            color: isPinned ? NAV_COLORS.ink : NAV_COLORS.onDarkMuted,
            backgroundColor: isPinned ? NAV_COLORS.onDark : 'transparent',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: isPinned ? NAV_COLORS.onDark : alpha(NAV_COLORS.onDark, 0.14),
              color: isPinned ? NAV_COLORS.ink : NAV_COLORS.onDark
            }
          }}
        >
          {isPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Stack
        direction="row"
        spacing={0.25}
        sx={{
          alignItems: 'center'
        }}
      >
        <Tooltip
          title={
            isAutoHideEnabled
              ? 'Auto show/hide is on: an unpinned menu opens when you reach the icons'
              : 'Auto show/hide is off: an unpinned menu opens only from the toggle'
          }
          placement="bottom-end"
        >
          <Stack
            direction="row"
            spacing={0.25}
            sx={{
              alignItems: 'center'
            }}
          >
            <Small sx={{ color: NAV_COLORS.onDarkMuted, fontSize: 11.5 }}>Auto show/hide</Small>
            <Switch
              checked={isAutoHideEnabled}
              onChange={toggleAutoHide}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: NAV_COLORS.onDark },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: NAV_COLORS.onDark,
                  opacity: 0.6
                },
                '& .MuiSwitch-switchBase': { color: alpha(NAV_COLORS.onDark, 0.7) },
                '& .MuiSwitch-track': { backgroundColor: alpha(NAV_COLORS.onDark, 0.4) }
              }}
              slotProps={{
                input: { 'aria-label': 'Auto show and hide the menu' }
              }}
            />
          </Stack>
        </Tooltip>

        {/* Collapsing from the far side of the screen meant a trip back to the rail. */}
        <Tooltip title="Collapse menu" placement="bottom-end">
          <IconButton
            onClick={() => setDrawerOpen(false)}
            size="small"
            aria-label="Collapse menu"
            sx={{
              color: NAV_COLORS.onDarkMuted,
              borderRadius: 1,
              '&:hover': { backgroundColor: alpha(NAV_COLORS.onDark, 0.14), color: NAV_COLORS.onDark }
            }}
          >
            <KeyboardDoubleArrowLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default SidebarPaneHeader;
